import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { getSchedules } from "../api";
import { ACTIVITIES } from "../constants/lessonPlanCatalog";
import AdminLessonPlan from "./AdminLessonPlan";
import { planRowsFromText } from "./DailyScheduleVisitModals";
import { centreIdOf, coachIdOf, dateKey, flattenSchedules, idOf, isStudent, scheduleDate } from "./dailyScheduleShared";

// View / Edit open one schedule on the Create Lesson Plan page (/daily-schedule/view|edit?id=&date=&balagruha=).
// A calendar card holds every coach visiting a Balgruha that day; one schedule is the coaches booked
// for the same time as the clicked visit (a lesson plan creates one copy per coach).

const momentOf = (time) => new Date(time).getTime();
const slotOf = (s) => `${momentOf(s.startTime)}|${momentOf(s.endTime)}`;
const courseKey = (label) => Object.keys(ACTIVITIES).find((k) => ACTIVITIES[k].label === label);
const nameKey = (name) => String(name || "").trim().toLowerCase();

export const scheduleSlot = (list, id, { userName = {}, children = [] } = {}) => {
  const target = list.find((s) => s._id === id);
  if (!target) return null;
  const date = dateKey(scheduleDate(target));
  const centreId = centreIdOf(target);
  const sameDay = list.filter((s) => centreIdOf(s) === centreId && dateKey(scheduleDate(s)) === date);
  // Same members as the calendar card: cancelled schedules only when the visit itself is cancelled.
  const pool = sameDay
    .filter((s) => (target.status === "cancelled" ? s.status === "cancelled" : s.status !== "cancelled"))
    .sort((a, b) => momentOf(a.startTime) - momentOf(b.startTime));
  const members = pool.filter((s) => slotOf(s) === slotOf(target));
  const nameOf = (s) => s.assignedToUser?.name || userName[coachIdOf(s)] || "Unassigned";

  // Other schedules at this Balgruha that day, so the page can switch between them.
  const bySlot = new Map();
  pool.forEach((s) => { if (!bySlot.has(slotOf(s))) bySlot.set(slotOf(s), []); bySlot.get(slotOf(s)).push(s); });
  const slots = [...bySlot.values()].map((group) => ({
    id: group[0]._id,
    startTime: group[0].startTime,
    endTime: group[0].endTime,
    coaches: [...new Set(group.map(nameOf))].join(", "),
    current: group.includes(target),
  }));

  // Coaches can edit their own copy of the plan, so the most recently saved copy is shown.
  const withPlan = members
    .filter((m) => m.lessonPlan?.children?.length)
    .sort((a, b) => new Date(b.updatedAt || 0) - new Date(a.updatedAt || 0));
  const lead = withPlan[0] || members.find((m) => planRowsFromText(m.description).length) || target;
  const saved = lead.lessonPlan?.children?.length ? lead.lessonPlan.children : planRowsFromText(lead.description);
  // Plans read from the description have names only; match them to the Balgruha's children.
  const byName = new Map(children.map((c) => [nameKey(c.name), c._id || c.id]));
  const seen = new Set();
  const rows = saved.map((r, i) => {
    const childId = idOf(r.childId) || byName.get(nameKey(r.childName)) || "";
    const rowId = childId && !seen.has(childId) ? childId : `row-${i}`;
    seen.add(rowId);
    return { ...r, childId, id: rowId };
  });

  const coachIds = [...new Set(members.map(coachIdOf).filter(Boolean))];
  const live = members.filter((m) => m.status !== "cancelled");
  const focus = lead.lessonPlan?.course || String(lead.learningsFocus || "").split("›").pop().trim();
  return {
    id,
    centreId,
    date,
    startTime: target.startTime,
    endTime: target.endTime,
    members,
    lead,
    rows,
    coaches: coachIds.map((cid) => ({ id: cid, name: nameOf(members.find((m) => coachIdOf(m) === cid)) })),
    status: !live.length ? "cancelled" : live.every((m) => m.status === "completed") ? "completed" : "active",
    course: courseKey(focus) || "",
    slots,
  };
};

export default function AdminLessonPlanSchedule({ mode, users, onDone, ...props }) {
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const id = query.get("id") || "";
  const date = query.get("date") || "";
  const balagruha = query.get("balagruha") || "";
  const [state, setState] = useState({ loading: true, list: [], failed: false });

  useEffect(() => {
    if (!id || !date || !balagruha) { setState({ loading: false, list: [], failed: false }); return undefined; }
    let cancelled = false;
    setState({ loading: true, list: [], failed: false });
    getSchedules({ balagruhaIds: [balagruha], startDate: date, endDate: date, status: [] })
      .then((result) => { if (!cancelled) setState({ loading: false, list: flattenSchedules(result), failed: false }); })
      .catch(() => { if (!cancelled) setState({ loading: false, list: [], failed: true }); });
    return () => { cancelled = true; };
  }, [id, date, balagruha]);

  const userName = useMemo(() => Object.fromEntries(users.map((u) => [u._id || u.id, u.name])), [users]);
  const children = useMemo(() => users.filter((u) => isStudent(u) && (u.balagruhaIds || []).some((b) => idOf(b) === balagruha)), [users, balagruha]);
  const schedule = useMemo(() => (state.loading ? null : scheduleSlot(state.list, id, { userName, children })), [state, id, userName, children]);
  const open = (scheduleId, nextMode = mode) => navigate(`/daily-schedule/${nextMode}?${new URLSearchParams({ id: scheduleId, date, balagruha })}`);

  if (state.loading) return <div className="ds-board__empty">Loading…</div>;
  if (!schedule) {
    return (
      <div className="lp-missing">
        <p>{state.failed ? "Unable to load the schedule. Please try again." : "This schedule could not be found. It may have been moved or deleted."}</p>
        <button type="button" className="lp-back" onClick={onDone}><ArrowLeft size={20} />Back to List</button>
      </div>
    );
  }
  return <AdminLessonPlan key={`${mode}|${id}`} mode={mode} existing={schedule} onOpen={open} users={users} onDone={onDone} {...props} />;
}
